import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../App';

// Vypočítá datum Velikonoční neděle pro daný rok (Gaussův algoritmus / Computus)
function velikonoceNedele(rok) {
  const a = rok % 19;
  const b = Math.floor(rok / 100);
  const c = rok % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const mesic = Math.floor((h + l - 7 * m + 114) / 31);
  const den = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(rok, mesic - 1, den);
}

function pridejDny(datum, dny) {
  const d = new Date(datum);
  d.setDate(d.getDate() + dny);
  return d;
}

function toISO(d) {
  return d.toISOString().split('T')[0];
}

// Vrátí Set ISO datumů svátků pro daný rok (státní + ostatní svátky)
function svatkyProRok(rok) {
  const nedele = velikonoceNedele(rok);
  const velkyPatek = toISO(pridejDny(nedele, -2));
  const velikonocniPondeli = toISO(pridejDny(nedele, 1));

  return new Set([
    `${rok}-01-01`, // Den obnovy samostatného českého státu / Nový rok
    velkyPatek,
    velikonocniPondeli,
    `${rok}-05-01`, // Svátek práce
    `${rok}-05-08`, // Den vítězství
    `${rok}-07-05`, // Cyril a Metoděj
    `${rok}-07-06`, // Jan Hus
    `${rok}-09-28`, // Den české státnosti
    `${rok}-10-28`, // Den vzniku Československa
    `${rok}-11-17`, // Den boje za svobodu a demokracii
    `${rok}-12-24`, // Štědrý den
    `${rok}-12-25`, // 1. svátek vánoční
    `${rok}-12-26`, // 2. svátek vánoční
  ]);
}

function jeSvatek(datumISO) {
  const rok = parseInt(datumISO.split('-')[0]);
  return svatkyProRok(rok).has(datumISO);
}

function pocetPracovnichDni(od, do_) {
  let count = 0;
  const cur = new Date(od);
  const end = new Date(do_);
  while (cur <= end) {
    const day = cur.getDay();
    const iso = toISO(cur);
    if (day !== 0 && day !== 6 && !jeSvatek(iso)) count++;
    cur.setDate(cur.getDate() + 1);
  }
  return count;
}

function formatDatum(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('cs-CZ');
}

const MESICE = ['Leden','Únor','Březen','Duben','Květen','Červen','Červenec','Srpen','Září','Říjen','Listopad','Prosinec'];

function StavBadge({ stav }) {
  const styles = {
    'čeká na schválení': { background: '#fff3cd', color: '#856404', border: '1px solid #ffc107' },
    'schváleno': { background: '#d1e7dd', color: '#0a3622', border: '1px solid #a3cfbb' },
    'zamítnuto': { background: '#f8d7da', color: '#58151c', border: '1px solid #f1aeb5' },
  };
  return (
    <span style={{ ...styles[stav], borderRadius: 6, padding: '2px 8px', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>
      {stav}
    </span>
  );
}

export default function Dovolena() {
  const { profile } = useAuth();
  const role = profile.role;
  const rok = new Date().getFullYear();
  const rocniNarok = profile.dovolena_narok ?? 20;

  const [mojeZadosti, setMojeZadosti] = useState([]);
  const [zbytek, setZbytek] = useState(rocniNarok);
  const [keSchvaleni, setKeSchvaleni] = useState([]);
  const [tymPrehled, setTymPrehled] = useState([]);
  const [tymMesic, setTymMesic] = useState('');
  const [vsechny, setVsechny] = useState([]);
  const [hledani, setHledani] = useState('');
  const [hledaniMesic, setHledaniMesic] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [datumOd, setDatumOd] = useState('');
  const [datumDo, setDatumDo] = useState('');
  const [poznamka, setPoznamka] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(true);

  const nactiMoje = useCallback(async () => {
    const { data } = await supabase
      .from('dovolena')
      .select('*, schvalil:schvalil_id(jmeno)')
      .eq('zamestnanec_id', profile.id)
      .eq('rok', rok)
      .order('datum_od', { ascending: false });
    setMojeZadosti(data || []);
    const cerpano = (data || []).filter(d => d.stav === 'schváleno').reduce((s, d) => s + d.pocet_dni, 0);
    setZbytek(rocniNarok - cerpano);
  }, [profile.id, rok, rocniNarok]);

  const nactiKeSchvaleni = useCallback(async () => {
    if (role !== 'vedouci') return;
    const { data: tym } = await supabase
      .from('zamestnanec_vedouci')
      .select('zamestnanec_id')
      .eq('vedouci_id', profile.id);
    const ids = (tym || []).map(t => t.zamestnanec_id);
    if (ids.length === 0) { setKeSchvaleni([]); return; }
    const { data } = await supabase
      .from('dovolena')
      .select('*, zamestnanec:zamestnanec_id(jmeno), schvalil:schvalil_id(jmeno)')
      .in('zamestnanec_id', ids)
      .eq('stav', 'čeká na schválení')
      .order('datum_od', { ascending: true });
    setKeSchvaleni(data || []);
  }, [profile.id, role]);

  const nactiTym = useCallback(async () => {
    if (role !== 'vedouci') return;
    const { data: tym } = await supabase
      .from('zamestnanec_vedouci')
      .select('zamestnanec_id')
      .eq('vedouci_id', profile.id);
    const ids = (tym || []).map(t => t.zamestnanec_id);
    if (ids.length === 0) { setTymPrehled([]); return; }
    const { data } = await supabase
      .from('dovolena')
      .select('*, zamestnanec:zamestnanec_id(jmeno), schvalil:schvalil_id(jmeno)')
      .in('zamestnanec_id', ids)
      .neq('stav', 'čeká na schválení')
      .eq('rok', rok)
      .order('datum_od', { ascending: false });
    setTymPrehled(data || []);
  }, [profile.id, role, rok]);

  const nactiVsechny = useCallback(async () => {
    if (role !== 'hr' && role !== 'jednatel') return;
    const { data } = await supabase
      .from('dovolena')
      .select('*, zamestnanec:zamestnanec_id(jmeno), schvalil:schvalil_id(jmeno)')
      .eq('rok', rok)
      .order('datum_od', { ascending: false });
    setVsechny(data || []);
  }, [role, rok]);

  useEffect(() => {
    const nacti = async () => {
      setLoading(true);
      await Promise.all([nactiMoje(), nactiKeSchvaleni(), nactiTym(), nactiVsechny()]);
      setLoading(false);
    };
    nacti();
  }, [nactiMoje, nactiKeSchvaleni, nactiTym, nactiVsechny]);

  const refreshAll = async () => {
    await Promise.all([nactiMoje(), nactiKeSchvaleni(), nactiTym(), nactiVsechny()]);
  };

  const podatZadost = async () => {
    setError('');
    if (!datumOd || !datumDo) { setError('Vyplňte datum od a do.'); return; }
    if (datumDo < datumOd) { setError('Datum do nemůže být před datem od.'); return; }
    const pocet = pocetPracovnichDni(datumOd, datumDo);
    if (pocet === 0) { setError('Zvolené období obsahuje pouze víkend a/nebo státní svátky – dovolenou na tyto dny nelze čerpat.'); return; }
    if (pocet > zbytek) { setError(`Nemáte dostatek dní. Zbývá vám ${zbytek} dní.`); return; }
    setSaving(true);
    const { error: err } = await supabase.from('dovolena').insert({
      zamestnanec_id: profile.id,
      datum_od: datumOd,
      datum_do: datumDo,
      pocet_dni: pocet,
      rok: new Date(datumOd).getFullYear(),
      poznamka: poznamka || null,
      stav: 'čeká na schválení',
    });
    setSaving(false);
    if (err) { setError('Nepodařilo se uložit žádost.'); return; }
    setSuccess('Žádost byla odeslána.');
    setShowForm(false);
    setDatumOd(''); setDatumDo(''); setPoznamka('');
    await refreshAll();
    setTimeout(() => setSuccess(''), 3000);
  };

  const zmenStav = async (id, stav) => {
    await supabase.from('dovolena').update({ stav, schvalil_id: profile.id }).eq('id', id);
    await refreshAll();
  };

  const smazat = async (id) => {
    if (!window.confirm('Opravdu smazat tuto žádost?')) return;
    await supabase.from('dovolena').delete().eq('id', id);
    await refreshAll();
  };

  const pocetOd = datumOd && datumDo && datumDo >= datumOd ? pocetPracovnichDni(datumOd, datumDo) : null;
  const obsahujeSvatek = datumOd && datumDo && datumDo >= datumOd ? (() => {
    const cur = new Date(datumOd);
    const end = new Date(datumDo);
    while (cur <= end) {
      if (jeSvatek(toISO(cur))) return true;
      cur.setDate(cur.getDate() + 1);
    }
    return false;
  })() : false;

  const vsechnyFiltr = vsechny.filter(d => {
    const jmeno = d.zamestnanec?.jmeno?.toLowerCase() || '';
    const mesicOk = hledaniMesic !== '' ? new Date(d.datum_od).getMonth() === parseInt(hledaniMesic) : true;
    const jmenoOk = hledani ? jmeno.includes(hledani.toLowerCase()) : true;
    return mesicOk && jmenoOk;
  });

  const tymFiltr = tymPrehled.filter(d => {
    return tymMesic !== '' ? new Date(d.datum_od).getMonth() === parseInt(tymMesic) : true;
  });

  if (loading) return <div style={{ color: '#888', padding: 32 }}>Načítám…</div>;

  return (
    <div>
      {/* Hlavička */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>Dovolená {rok}</h2>
          <div style={{ marginTop: 6, fontSize: 14, color: '#555' }}>
            Nárok: <strong>{rocniNarok} dní</strong> &nbsp;·&nbsp;
            Zbývá: <strong style={{ color: zbytek <= 5 ? '#c0392b' : '#2d6a4f' }}>{zbytek} dní</strong>
          </div>
        </div>
        <button className="btn-primary" onClick={() => { setShowForm(true); setError(''); }}>
          + Žádat o dovolenou
        </button>
      </div>

      {success && (
        <div style={{ background: '#d1e7dd', color: '#0a3622', border: '1px solid #a3cfbb', borderRadius: 10, padding: '10px 16px', marginBottom: 16, fontSize: 14 }}>
          ✅ {success}
        </div>
      )}

      {/* Formulář */}
      {showForm && (
        <div style={{ background: '#fff', borderRadius: 14, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', padding: 20, marginBottom: 24 }}>
          <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 700 }}>Nová žádost o dovolenou</h3>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
            <div>
              <label style={{ display: 'block', fontSize: 13, color: '#555', marginBottom: 4 }}>Datum od</label>
              <input type="date" value={datumOd} onChange={e => setDatumOd(e.target.value)}
                style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 14, fontFamily: 'DM Sans, sans-serif' }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 13, color: '#555', marginBottom: 4 }}>Datum do</label>
              <input type="date" value={datumDo} onChange={e => setDatumDo(e.target.value)}
                style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 14, fontFamily: 'DM Sans, sans-serif' }} />
            </div>
          </div>
          {pocetOd !== null && (
            <div style={{ fontSize: 13, color: '#2d6a4f', marginBottom: 8, fontWeight: 600 }}>
              Počet pracovních dní: {pocetOd}
            </div>
          )}
          {obsahujeSvatek && (
            <div style={{ fontSize: 13, color: '#856404', marginBottom: 12, background: '#fff3cd', border: '1px solid #ffc107', borderRadius: 8, padding: '8px 12px' }}>
              ℹ️ Vybrané období obsahuje státní svátek – tento den se do dovolené nezapočítává.
            </div>
          )}
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: 13, color: '#555', marginBottom: 4 }}>Poznámka (volitelné)</label>
            <textarea value={poznamka} onChange={e => setPoznamka(e.target.value)} rows={2}
              style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 14, fontFamily: 'DM Sans, sans-serif', resize: 'vertical', boxSizing: 'border-box' }} />
          </div>
          {error && (
            <div style={{ background: '#f8d7da', color: '#58151c', borderRadius: 8, padding: '8px 12px', marginBottom: 12, fontSize: 13 }}>
              {error}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn-primary" onClick={podatZadost} disabled={saving}>
              {saving ? 'Ukládám…' : 'Odeslat žádost'}
            </button>
            <button onClick={() => { setShowForm(false); setError(''); }}
              style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #ddd', background: '#fff', cursor: 'pointer', fontSize: 14, fontFamily: 'DM Sans, sans-serif' }}>
              Zrušit
            </button>
          </div>
        </div>
      )}

      {/* MOJE ŽÁDOSTI */}
      <div style={{ background: '#fff', borderRadius: 14, boxShadow: '0 1px 6px rgba(0,0,0,0.07)', padding: 20, marginBottom: 24 }}>
        <h3 style={{ margin: '0 0 14px', fontSize: 15, fontWeight: 700 }}>Moje žádosti</h3>
        {mojeZadosti.length === 0 ? (
          <div style={{ color: '#888', fontSize: 14 }}>Žádné žádosti pro tento rok.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {mojeZadosti.map(d => (
              <div key={d.id} style={{ borderRadius: 10, border: '1px solid #f0f0f0', padding: '12px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>
                    {formatDatum(d.datum_od)} – {formatDatum(d.datum_do)} &nbsp;·&nbsp; {d.pocet_dni} {d.pocet_dni === 1 ? 'den' : 'dní'}
                  </div>
                  {d.poznamka && <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{d.poznamka}</div>}
                  {d.schvalil && <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>Vyřídil: {d.schvalil.jmeno}</div>}
                </div>
                <StavBadge stav={d.stav} />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* VEDOUCÍ – KE SCHVÁLENÍ */}
      {role === 'vedouci' && (
        <div style={{ background: '#fff', borderRadius: 14, boxShadow: '0 1px 6px rgba(0,0,0,0.07)', padding: 20, marginBottom: 24 }}>
          <h3 style={{ margin: '0 0 14px', fontSize: 15, fontWeight: 700, color: '#856404' }}>
            ⏳ Žádosti ke schválení
            {keSchvaleni.length > 0 && (
              <span style={{ background: '#ffc107', color: '#000', borderRadius: 10, padding: '1px 8px', fontSize: 12, marginLeft: 8 }}>
                {keSchvaleni.length}
              </span>
            )}
          </h3>
          {keSchvaleni.length === 0 ? (
            <div style={{ color: '#888', fontSize: 14 }}>Žádné nevyřízené žádosti.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {keSchvaleni.map(d => (
                <div key={d.id} style={{ borderRadius: 10, border: '1px solid #fff3cd', background: '#fffdf0', padding: '14px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>{d.zamestnanec?.jmeno}</div>
                    <div style={{ fontSize: 13, color: '#555', marginTop: 2 }}>
                      {formatDatum(d.datum_od)} – {formatDatum(d.datum_do)} &nbsp;·&nbsp; <strong>{d.pocet_dni} {d.pocet_dni === 1 ? 'den' : 'dní'}</strong>
                    </div>
                    {d.poznamka && <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{d.poznamka}</div>}
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => zmenStav(d.id, 'schváleno')}
                      style={{ background: '#2d6a4f', color: '#fff', border: 'none', borderRadius: 8, padding: '7px 14px', fontSize: 13, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', fontWeight: 600 }}>
                      ✓ Schválit
                    </button>
                    <button onClick={() => zmenStav(d.id, 'zamítnuto')}
                      style={{ background: '#fff', color: '#c0392b', border: '1px solid #c0392b', borderRadius: 8, padding: '7px 14px', fontSize: 13, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', fontWeight: 600 }}>
                      ✕ Zamítnout
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* VEDOUCÍ – PŘEHLED TÝMU */}
      {role === 'vedouci' && (
        <div style={{ background: '#fff', borderRadius: 14, boxShadow: '0 1px 6px rgba(0,0,0,0.07)', padding: 20, marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>Přehled dovolených týmu</h3>
            <select value={tymMesic} onChange={e => setTymMesic(e.target.value)}
              style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid #ddd', fontSize: 13, fontFamily: 'DM Sans, sans-serif' }}>
              <option value="">Celý rok</option>
              {MESICE.map((m, i) => <option key={i} value={i}>{m}</option>)}
            </select>
          </div>
          {tymFiltr.length === 0 ? (
            <div style={{ color: '#888', fontSize: 14 }}>Žádné záznamy.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {tymFiltr.map(d => (
                <div key={d.id} style={{ borderRadius: 10, border: '1px solid #f0f0f0', padding: '12px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>{d.zamestnanec?.jmeno}</div>
                    <div style={{ fontSize: 13, color: '#555', marginTop: 2 }}>
                      {formatDatum(d.datum_od)} – {formatDatum(d.datum_do)} &nbsp;·&nbsp; {d.pocet_dni} {d.pocet_dni === 1 ? 'den' : 'dní'}
                    </div>
                    {d.schvalil && <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>Schválil: {d.schvalil.jmeno}</div>}
                  </div>
                  <StavBadge stav={d.stav} />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* HR / JEDNATEL – CELOFIREMNÍ PŘEHLED */}
      {(role === 'hr' || role === 'jednatel') && (
        <div style={{ background: '#fff', borderRadius: 14, boxShadow: '0 1px 6px rgba(0,0,0,0.07)', padding: 20, marginBottom: 24 }}>
          <h3 style={{ margin: '0 0 14px', fontSize: 15, fontWeight: 700 }}>Celofiremní přehled dovolených</h3>
          <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
            <input
              type="text"
              placeholder="Hledat podle jména…"
              value={hledani}
              onChange={e => setHledani(e.target.value)}
              style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 14, fontFamily: 'DM Sans, sans-serif', minWidth: 200 }}
            />
            <select value={hledaniMesic} onChange={e => setHledaniMesic(e.target.value)}
              style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #ddd', fontSize: 13, fontFamily: 'DM Sans, sans-serif' }}>
              <option value="">Celý rok</option>
              {MESICE.map((m, i) => <option key={i} value={i}>{m}</option>)}
            </select>
          </div>
          {vsechnyFiltr.length === 0 ? (
            <div style={{ color: '#888', fontSize: 14 }}>Žádné záznamy.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {vsechnyFiltr.map(d => (
                <div key={d.id} style={{ borderRadius: 10, border: '1px solid #f0f0f0', padding: '12px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>{d.zamestnanec?.jmeno}</div>
                    <div style={{ fontSize: 13, color: '#555', marginTop: 2 }}>
                      {formatDatum(d.datum_od)} – {formatDatum(d.datum_do)} &nbsp;·&nbsp; {d.pocet_dni} {d.pocet_dni === 1 ? 'den' : 'dní'}
                    </div>
                    {d.poznamka && <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{d.poznamka}</div>}
                    {d.schvalil && <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>Vyřídil: {d.schvalil.jmeno}</div>}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <StavBadge stav={d.stav} />
                    {d.stav === 'čeká na schválení' && (
                      <>
                        <button onClick={() => zmenStav(d.id, 'schváleno')}
                          style={{ background: '#2d6a4f', color: '#fff', border: 'none', borderRadius: 8, padding: '5px 12px', fontSize: 12, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', fontWeight: 600 }}>
                          ✓ Schválit
                        </button>
                        <button onClick={() => zmenStav(d.id, 'zamítnuto')}
                          style={{ background: '#fff', color: '#c0392b', border: '1px solid #c0392b', borderRadius: 8, padding: '5px 12px', fontSize: 12, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', fontWeight: 600 }}>
                          ✕ Zamítnout
                        </button>
                      </>
                    )}
                    {role === 'hr' && (
                      <button onClick={() => smazat(d.id)}
                        style={{ background: '#fff', color: '#999', border: '1px solid #ddd', borderRadius: 8, padding: '5px 10px', fontSize: 12, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' }}>
                        🗑
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
